import React, { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from "recharts";
import { formatNumber } from "utils/helpers";

interface Series {
  key: string;
  color: string;
}

interface Props {
  data: any[];
  series: Series[];
  height?: number;
  valueLabel: string;
}

const RechartsGroupedBar: React.FC<Props> = ({
  data,
  series,
  height = 360,
  valueLabel
}) => {
  const [hovered, setHovered] = useState<{
    tierIndex: number;
    brandKey: string;
  } | null>(null);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        margin={{ top: 10, right: 20, left: 10, bottom: 50 }}
        barCategoryGap={24}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.4} />

        <XAxis
          dataKey="tierLabel"
          interval={0}
          tick={({ x, y, payload }) => {
            const row = data[payload.index];
            return (
              <g transform={`translate(${x},${y})`}>
                <text
                  y={14}
                  textAnchor="middle"
                  fontSize={13}
                  fontWeight={600}
                >
                  {payload.value}
                </text>
                <text
                  y={30}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#94a3b8"
                >
                  ({row.tierRange})
                </text>
              </g>
            );
          }}
        />

        <YAxis tickFormatter={formatNumber} />

        <Tooltip
          cursor={false}
          content={() => {
            if (!hovered) return null;

            const row = data[hovered.tierIndex];
            const value = row[hovered.brandKey];

            return (
              <div
                style={{
                  background: "#fff",
                  padding: 12,
                  borderRadius: 10,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
                  minWidth: 200
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {hovered.brandKey}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {row.tierLabel} ({row.tierRange})
                </div>
                <div style={{ marginTop: 6, fontWeight: 600 }}>
                  {formatNumber(value)} {valueLabel}
                </div>
              </div>
            );
          }}
        />

        {series.map(s => (
          <Bar
            key={s.key}
            dataKey={s.key}
            fill={s.color}
            radius={[6, 6, 0, 0]}
            maxBarSize={44}
            opacity={
              !hovered || hovered.brandKey === s.key ? 1 : 0.3
            }
            onMouseEnter={(_, index) =>
              setHovered({ tierIndex: index, brandKey: s.key })
            }
            onMouseLeave={() => setHovered(null)}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default RechartsGroupedBar;
